import { Search, X } from 'lucide-react';
import { DatePicker } from '@/components/ui/DatePicker';
import { Select } from '@/components/ui/Select';
import { cn } from '@/lib/utils';

export type RecordingTabType = 'all' | 'subject' | 'date';

interface RecordedClassesFilterBarProps {
  activeTab: RecordingTabType;
  onTabChange: (tab: RecordingTabType) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  showAdvancedFilters?: boolean;
  onToggleAdvancedFilters?: () => void;
  topic: string;
  onTopicChange: (topic: string) => void;
  topicOptions: string[];
  onDate: string;
  onDateChange: (date: string) => void;
  onClearFilters?: () => void;
  isFiltered?: boolean;
}

export function RecordedClassesFilterBar({
  activeTab,
  onTabChange,
  searchQuery,
  onSearchChange,
  topic,
  onTopicChange,
  topicOptions,
  onDate,
  onDateChange,
}: RecordedClassesFilterBarProps) {
  const tabs: { id: RecordingTabType; label: string }[] = [
    { id: 'all', label: 'All Recordings' },
    { id: 'subject', label: 'By Subject' },
    { id: 'date', label: 'By Date' },
  ];

  return (
    <div className="card bg-surface border border-line rounded-[16px] p-2.5 sm:px-4 sm:py-2.5 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-2xs">
      {/* 1. Status Tabs */}
      <div className="flex items-center gap-4 sm:gap-6 overflow-x-auto no-scrollbar border-b md:border-b-0 border-line pb-2 md:pb-0 scroll-smooth">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              className={cn(
                'text-[14px] font-bold transition-all relative py-1 cursor-pointer whitespace-nowrap',
                isActive ? 'text-indigo-400 font-extrabold' : 'text-ink-3 hover:text-ink',
              )}
            >
              {tab.label}
              {isActive && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      {/* 2. Filters & Search - Date Picker is placed right before the search input */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2.5 w-full md:w-auto">
        {/* Optional Chapter/Topic selector */}
        {topicOptions.length > 0 && (
          <div className="w-full sm:w-36 md:w-40 shrink-0">
            <Select
              hideLabel
              label="Chapter"
              placeholder="All chapters"
              options={[
                { value: '', label: 'All chapters' },
                ...topicOptions.map((opt) => ({ value: opt, label: opt })),
              ]}
              value={topic}
              onChange={(e) => onTopicChange(e.target.value)}
              className="h-9 w-full text-sm font-semibold rounded-field"
            />
          </div>
        )}

        {/* Date filter placed right BEFORE the search */}
        <div className="w-full sm:w-36 md:w-40 shrink-0">
          <DatePicker
            hideLabel
            label="Posted on"
            size="sm"
            placeholder="Select date"
            value={onDate}
            onChange={(e) => onDateChange(e.target.value)}
            isClearable
            className="h-9 w-full text-sm font-medium rounded-field"
          />
        </div>

        {/* Search input */}
        <div className="relative w-full sm:w-44 md:w-56">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-3 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search recordings..."
            className={cn(
              'h-9 w-full pl-8 text-sm bg-surface-2 border border-line rounded-field text-ink placeholder:text-sm placeholder:text-ink-3 focus:outline-none focus:border-indigo-500 transition-colors',
              searchQuery ? 'pr-8' : 'pr-2.5',
            )}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-ink-3 hover:text-ink transition-colors cursor-pointer"
              title="Clear search"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
